import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { Alert, Button, Card, Field, Spinner, StatusPill } from '../../components/ui';
import { useToast } from '../../components/Toast';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const SLOTS = ['morning', 'afternoon', 'evening', 'overnight'];

const EMPTY = {
  firstName: '', lastName: '', phone: '', dateOfBirth: '',
  addressLine1: '', addressLine2: '', suburb: '', city: '', region: '', postcode: '', country: '',
  yearsExperience: 0, workExperience: '', qualifications: '', nannyExperience: '', ageGroups: '',
  hasDriversLicence: false, hasOwnTransport: false, firstAidCertified: false,
  availability: {}, availabilityNotes: '', emergencyContactName: '', emergencyContactPhone: '', about: '',
};

export default function NannyApplication() {
  const { t } = useTranslation();
  const toast = useToast();
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  /** Copies only the editable fields out of the API response into the form. */
  function hydrate({ nanny, checklist: list }) {
    const next = { ...EMPTY };
    Object.keys(EMPTY).forEach((key) => {
      if (nanny.profile[key] !== undefined) next[key] = nanny.profile[key];
    });
    setForm({ ...next, firstName: nanny.firstName, lastName: nanny.lastName, phone: nanny.phone });
    setStatus(nanny.profile);
    setChecklist(list);
  }

  useEffect(() => {
    api
      .get('/api/nanny/profile')
      .then(hydrate)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [toast]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const setCheck = (key) => (e) => setForm({ ...form, [key]: e.target.checked });

  function toggleSlot(day, slot) {
    const current = form.availability[day] || [];
    const next = current.includes(slot) ? current.filter((s) => s !== slot) : [...current, slot];
    setForm({ ...form, availability: { ...form.availability, [day]: next } });
  }

  async function save(event) {
    event?.preventDefault();
    setSaving(true);
    setFieldErrors({});
    try {
      const result = await api.put('/api/nanny/profile', { ...form, availability: form.availability || {} });
      hydrate(result);
      toast.success(t('nannyApplication.savedDetails'));
      return true;
    } catch (err) {
      toast.error(err.message);
      setFieldErrors(err.fieldErrors || {});
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function submitApplication() {
    setSubmitting(true);
    try {
      if (!(await save())) return;
      const { nanny } = await api.post('/api/nanny/application/submit');
      setStatus(nanny.profile);
      toast.success(t('nannyApplication.submittedSuccess'));
      const refreshed = await api.get('/api/nanny/profile');
      setChecklist(refreshed.checklist);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <Spinner />;

  const locked = status?.applicationStatus === 'pending_review';
  const outstanding = checklist.filter((c) => !c.done);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('nannyApplication.pageTitle')}</h1>
          <p className="muted">{t('nannyApplication.subtitle')}</p>
        </div>
        {status && <StatusPill status={status.applicationStatus} label={status.applicationStatusLabel} />}
      </div>

      {status?.adminMessage && (
        <Alert kind="info">
          <strong>{t('nannyApplication.messageFromTeam')} </strong>
          {status.adminMessage}
        </Alert>
      )}
      {locked && <Alert kind="info">{t('nannyApplication.underReview')}</Alert>}

      <form onSubmit={save}>
        <Card title={t('nannyApplication.personalInfo')}>
          <div className="grid-2">
            <Field label={t('nannyApplication.firstName')} required error={fieldErrors.firstName}>
              <input value={form.firstName} onChange={set('firstName')} required />
            </Field>
            <Field label={t('nannyApplication.lastName')} required error={fieldErrors.lastName}>
              <input value={form.lastName} onChange={set('lastName')} required />
            </Field>
            <Field label={t('nannyApplication.phone')} error={fieldErrors.phone}>
              <input value={form.phone} onChange={set('phone')} />
            </Field>
            <Field label={t('nannyApplication.dateOfBirth')}>
              <input type="date" value={form.dateOfBirth || ''} onChange={set('dateOfBirth')} />
            </Field>
          </div>
          <Field label={t('nannyApplication.aboutYou')} hint={t('nannyApplication.aboutYouHint')}>
            <textarea rows={4} value={form.about} onChange={set('about')} />
          </Field>
        </Card>

        <Card title={t('nannyApplication.address')}>
          <Field label={t('nannyApplication.streetAddress')}>
            <input value={form.addressLine1} onChange={set('addressLine1')} />
          </Field>
          <Field label={t('nannyApplication.aptSuite')}>
            <input value={form.addressLine2} onChange={set('addressLine2')} />
          </Field>
          <div className="grid-2">
            <Field label={t('nannyApplication.suburb')}><input value={form.suburb} onChange={set('suburb')} /></Field>
            <Field label={t('nannyApplication.city')}><input value={form.city} onChange={set('city')} /></Field>
            <Field label={t('nannyApplication.region')}><input value={form.region} onChange={set('region')} /></Field>
            <Field label={t('nannyApplication.postcode')}><input value={form.postcode} onChange={set('postcode')} /></Field>
          </div>
          <Field label={t('nannyApplication.country')}><input value={form.country} onChange={set('country')} /></Field>
        </Card>

        <Card title={t('nannyApplication.experienceQualifications')}>
          <Field label={t('nannyApplication.yearsExperience')}>
            <input
              type="number"
              min="0"
              max="70"
              value={form.yearsExperience}
              onChange={(e) => setForm({ ...form, yearsExperience: e.target.value })}
            />
          </Field>
          <Field label={t('nannyApplication.workExperience')} hint={t('nannyApplication.workExperienceHint')}>
            <textarea rows={4} value={form.workExperience} onChange={set('workExperience')} />
          </Field>
          <Field label={t('nannyApplication.nannyExperience')} hint={t('nannyApplication.nannyExperienceHint')}>
            <textarea rows={4} value={form.nannyExperience} onChange={set('nannyExperience')} />
          </Field>
          <Field label={t('nannyApplication.qualifications')} hint={t('nannyApplication.qualificationsHint')}>
            <textarea rows={3} value={form.qualifications} onChange={set('qualifications')} />
          </Field>
          <Field label={t('nannyApplication.ageGroups')} hint={t('nannyApplication.ageGroupsHint')}>
            <input value={form.ageGroups} onChange={set('ageGroups')} />
          </Field>
          <div className="check-row">
            <label className="check">
              <input type="checkbox" checked={!!form.hasDriversLicence} onChange={setCheck('hasDriversLicence')} />
              {t('nannyApplication.driversLicence')}
            </label>
            <label className="check">
              <input type="checkbox" checked={!!form.hasOwnTransport} onChange={setCheck('hasOwnTransport')} />
              {t('nannyApplication.ownTransport')}
            </label>
            <label className="check">
              <input type="checkbox" checked={!!form.firstAidCertified} onChange={setCheck('firstAidCertified')} />
              {t('nannyApplication.firstAidCertified')}
            </label>
          </div>
        </Card>

        <Card title={t('nannyApplication.availability')} subtitle={t('nannyApplication.availabilitySubtitle')}>
          <div className="availability">
            <div className="availability-row availability-head">
              <span />
              {SLOTS.map((slot) => (
                <span key={slot}>{t(`nannyApplication.slots.${slot}`)}</span>
              ))}
            </div>
            {DAYS.map((day) => (
              <div className="availability-row" key={day}>
                <span className="day">{t(`nannyApplication.days.${day}`)}</span>
                {SLOTS.map((slot) => (
                  <label key={slot} className="slot">
                    <input
                      type="checkbox"
                      checked={(form.availability[day] || []).includes(slot)}
                      onChange={() => toggleSlot(day, slot)}
                      aria-label={`${day} ${slot}`}
                    />
                  </label>
                ))}
              </div>
            ))}
          </div>
          <Field label={t('nannyApplication.availabilityNotes')}>
            <textarea rows={2} value={form.availabilityNotes} onChange={set('availabilityNotes')} />
          </Field>
        </Card>

        <Card title={t('nannyApplication.emergencyContact')}>
          <div className="grid-2">
            <Field label={t('nannyApplication.emergencyContactName')}>
              <input value={form.emergencyContactName} onChange={set('emergencyContactName')} />
            </Field>
            <Field label={t('nannyApplication.emergencyContactPhone')}>
              <input value={form.emergencyContactPhone} onChange={set('emergencyContactPhone')} />
            </Field>
          </div>
        </Card>

        <div className="form-footer">
          <Button type="submit" variant="secondary" loading={saving}>{t('nannyApplication.saveChanges')}</Button>
          {status?.applicationStatus !== 'approved' && (
            <Button type="button" onClick={submitApplication} loading={submitting} disabled={locked}>
              {locked ? t('nannyApplication.submittedForReview') : t('nannyApplication.submitApplication')}
            </Button>
          )}
        </div>
        {outstanding.length > 0 && !locked && (
          <p className="muted small">
            {t('nannyApplication.stillToComplete', { items: outstanding.map((o) => t(`nannyDashboard.checklist.${o.key}`)).join(', ') })}
          </p>
        )}
      </form>
    </>
  );
}
